import { IFrame } from "client/Components";
import {
	useEffectSkipFirstRender,
	useForceRerender,
	useResizeObserver,
	useSynchronizedRef
} from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { powerBiAppIframePermissions } from "../../LeftSliders/EditTile/EditPowerBiTile";
import styles from "./PowerBiEmbed.module.less";

export type PowerBiEmbedProps = {
	embedUrlBase: string;
	dashboardId: string;
	tileId: string;
	accessToken: string;
	trackingId: string;
	onLoaded?: (message: PowerBiMessage) => void;
	onError?: () => void;
};

export const PowerBiEmbed = (props: PowerBiEmbedProps): ReactElement | null => {
	const {
		embedUrlBase,
		dashboardId,
		tileId,
		accessToken,
		trackingId,
		onLoaded,
		onError
	} = props;

	const [hasError, setHasError] = useState<boolean>(false);

	const ref: RefObject<HTMLIFrameElement | null> = useRef<HTMLIFrameElement>(null);

	const dimensionInfoRef: RefObject<DimensionInfo> = useRef<DimensionInfo>({
		issued: {
			width: MIN_WIDTH,
			height: MIN_HEIGHT
		},
		requested: {
			width: MIN_WIDTH,
			height: MIN_HEIGHT
		}
	});

	const wasLoadedOnceRef: RefObject<boolean> = useRef<boolean>(false);

	const [reload, version] = useForceRerender();

	const url: string = `${embedUrlBase}?dashboardId=${dashboardId}&tileId=${tileId}`;

	const {
		width,
		height
	} = useResizeObserver(ref);

	const isVisible: boolean = width > 0 && height > 0;

	const powerBiDimension = useMemo(
		(): Dimension => {
			return {
				width: Math.max(width, MIN_WIDTH),
				height: Math.max(height, MIN_HEIGHT)
			};
		},
		[height, width]
	);

	const onLoadedRef: ReadonlyRefObject<typeof onLoaded> = useSynchronizedRef(onLoaded);
	const onErrorRef: ReadonlyRefObject<typeof onError> = useSynchronizedRef(onError);

	const loadHandler = useCallback(
		(): void => {
			const { current: iframe } = ref;
			if (!iframe) {
				return;
			}

			const { width, height } = powerBiDimension;

			iframe.contentWindow
				?.postMessage(
					JSON.stringify({
						action: "loadTile",
						accessToken: accessToken,
						width,
						height
					}),
					"*"
				);

			dimensionInfoRef.current = {
				issued: { width, height },
				requested: { width, height }
			};
		},
		[
			accessToken,
			powerBiDimension
		]
	);

	useEffect(
		(): (() => void) | undefined => {
			if (hasError) {
				return;
			}

			const messageHandler = (args: MessageEvent): void => {
				const { current: iframe } = ref;
				if (!iframe) {
					return;
				}

				if (args.source !== iframe.contentWindow) {
					return;
				}

				let data: PowerBiMessage | undefined;
				if (args.data) {
					// see #28753, sometimes PowerBi doesn't return (valid) JSON
					// therefore we first try to parse the JSON, if this doesn't work we try the body property
					// if that also fails, we don't show the option to click on the tile
					if (typeof args.data === "string") {
						try {
							data = JSON.parse(args.data);
						}
						catch (e) {
							console.error(e);
						}
					} else if (typeof args.data === "object") {
						if (args.data.hasOwnProperty("body")) {
							data = args.data.body;
						}
					}
				}

				if (!data) {
					return;
				}

				//message structure
				//	event: "tileLoaded", "error" ...
				//	error: 401, 403, 404 ...
				//		401 - unauthorized: no permissions or license for powerbi
				//		404 - not found: tile was removed or wrong: url or powerBiTileId or dashboardId
				//		403 - forbidden: probably token has been expired
				//	tileData: jwt
				switch (data.event) {
					// NOTE: the tileLoaded event will be fired for every resize as well (so it is not a 1 time event after the
					// initial loading)
					case "tileLoaded":
						//it is strange that "tileLoaded" event contains info about errors and "error" event not fired
						if (data.error) {
							onErrorRef.current?.();
							setHasError(true);
							return;
						}

						if (!wasLoadedOnceRef.current) {
							const { issued, requested } = dimensionInfoRef.current;
							const hasSizeChangedSinceWeStartedLoading: boolean = requested.width !== issued.width
								|| requested.height !== issued.height;

							if (hasSizeChangedSinceWeStartedLoading) {
								const {
									width,
									height
								} = requested;

								// It appears that request for resize are ignored while the tile is still loading,
								// so we re-send the request once it is loaded
								iframe.contentWindow
									?.postMessage(
										JSON.stringify({
											action: "resizeTile",
											width,
											height
										}),
										"*"
									);
							}

							wasLoadedOnceRef.current = true;

							onLoadedRef.current?.(data);
						}

						break;
					case "error":
						setHasError(true);
						onErrorRef.current?.();
						break;
					default:
						// nothing to do
						break;
				}
			};

			window.addEventListener("message", messageHandler);

			return (): void => {
				window.removeEventListener("message", messageHandler);
			};
		},
		[
			hasError,
			onErrorRef,
			onLoadedRef
		]
	);

	useEffectSkipFirstRender(
		(): void => {
			hasImplicitDependencyOn(accessToken);
			reload();
		},
		[accessToken, reload]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(url);
			wasLoadedOnceRef.current = false;

			dimensionInfoRef.current = {
				issued: {
					width: MIN_WIDTH,
					height: MIN_HEIGHT
				},
				requested: {
					width: MIN_WIDTH,
					height: MIN_HEIGHT
				}
			};

			setHasError(false);
		},
		[url]
	);

	useEffect(
		(): void => {
			const { current: iframe } = ref;
			if (!iframe) {
				return;
			}

			if (!isVisible) {
				return;
			}

			const { width, height } = powerBiDimension;

			dimensionInfoRef.current
				.requested = {
					width,
					height
				};

			if (!wasLoadedOnceRef.current) {
				return;
			}

			iframe.contentWindow
				?.postMessage(
					JSON.stringify({
						action: "resizeTile",
						width,
						height
					}),
					"*"
				);
		},
		[powerBiDimension, isVisible]
	);

	if (hasError) {
		return null;
	}

	return (
		<IFrame
			className={styles.root}
			ref={ref}
			src={url}
			permissions={powerBiAppIframePermissions}
			trackingId={trackingId}
			onLoad={loadHandler}
			key={version}
		/>
	);
};
PowerBiEmbed.displayName = "PowerBiEmbed";

type Dimension = {
	width: number;
	height: number;
};

type DimensionInfo = {
	issued: Dimension;
	requested: Dimension;
};

export type PowerBiMessage = {
	event: string;
	error: number;
	tileData: any;
};

// PowerBi JS's crashes when the embed size is too small
const MIN_WIDTH: number = 100;
const MIN_HEIGHT: number = 100;

export function decodePowerBiBase64(str: string): string {
	// Taken from powerbivisualrenderer.min.js
	return decodeURIComponent(escape(atob(str)));
}
